import { UserAddOutlined } from '@ant-design/icons'
import { required } from '@community_dev/pixels'
import { REGISTRATION_SOURCE, RegistrationSource } from '@community_dev/types/lib/api/RegistrationSource'
import { SMS_PROVIDER, SmsProvider } from '@community_dev/types/lib/api/SmsProvider'
import { useMutation } from '@tanstack/react-query'
import { Button, Input, Modal, Select, Typography, notification } from 'antd'
import { useState, type JSX } from 'react'
import { Field, Form } from 'react-final-form'

import { postCreateClientV2 } from 'api/clients'

type NewClientButtonProps = {
  onCreate?: () => void
}

type CampaignConnectivityPartner = Exclude<SmsProvider, typeof SMS_PROVIDER.NOT_SET>

const registrationSourceOptions = [{ label: 'Community CSP', value: REGISTRATION_SOURCE.COMMUNITY }]

const campaignConnectivityPartnerOptions = [{ label: 'Infobip', value: SMS_PROVIDER.INFOBIP }]

const initialValues = {
  registration_source: REGISTRATION_SOURCE.COMMUNITY,
  campaign_connectivity_partner: SMS_PROVIDER.INFOBIP,
}

export const NewClientButton = ({ onCreate }: NewClientButtonProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)

  const { mutateAsync: createClient } = useMutation(postCreateClientV2, {
    onSuccess() {
      notification.success({
        message: 'Client created.',
        placement: 'bottomRight',
        duration: 2,
      })
      onCreate?.()
      setIsOpen(false)
    },
    onError() {
      notification.error({
        message: 'Could not create client. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleCreateClient = (values: {
    first_name: string
    last_name?: string
    registration_source?: RegistrationSource
    campaign_connectivity_partner: CampaignConnectivityPartner
  }) => {
    const body = {
      first_name: values.first_name.trim(),
      last_name: values.last_name?.trim(),
      registration_source: values.registration_source,
      sms_provider: values.campaign_connectivity_partner,
    }
    return createClient({ body })
  }

  return (
    <>
      <Button icon={<UserAddOutlined />} onClick={() => setIsOpen(true)} size="large" type="primary">
        New Client
      </Button>
      {isOpen && (
        <Form
          initialValues={initialValues}
          onSubmit={handleCreateClient}
          render={({ handleSubmit, submitting, valid }) => (
            <Modal
              confirmLoading={submitting}
              okButtonProps={{ disabled: !valid }}
              okText="Create"
              onCancel={() => setIsOpen(false)}
              onOk={handleSubmit}
              open={isOpen}
              title="Create New Client"
            >
              <form onSubmit={handleSubmit}>
                <Field name="first_name" validate={required}>
                  {({ input, meta }) => (
                    <>
                      <label htmlFor="first_name">
                        First name
                        <Input id="first_name" type="text" {...input} />
                      </label>
                      {!meta.active && meta.touched && meta.modified && meta.error && (
                        <div>
                          <Typography.Text type="danger">First name {meta.error}</Typography.Text>
                        </div>
                      )}
                    </>
                  )}
                </Field>

                <Field name="last_name">
                  {({ input, meta }) => (
                    <>
                      <label htmlFor="last_name">
                        Last name
                        <Input id="last_name" type="text" {...input} />
                      </label>
                      {!meta.active && meta.touched && meta.modified && meta.error && (
                        <div>
                          <Typography.Text type="danger">Last name {meta.error}</Typography.Text>
                        </div>
                      )}
                    </>
                  )}
                </Field>
                <Field name="registration_source">
                  {({ input }) => (
                    <>
                      <label htmlFor="registration_source">
                        TCR Registration
                        <Select
                          allowClear
                          data-testid="registration-source-select"
                          disabled
                          id="registration_source"
                          {...input}
                          options={registrationSourceOptions}
                          placeholder="Select a registration source"
                          style={{ display: 'block', width: '100%' }}
                        />
                      </label>
                      <Typography.Text type="secondary">Cannot be changed after creation</Typography.Text>
                    </>
                  )}
                </Field>
                <Field name="campaign_connectivity_partner" validate={required}>
                  {({ input }) => (
                    <label htmlFor="campaign_connectivity_partner" style={{ display: 'block', marginTop: '8px' }}>
                      Campaign Connectivity Partner (CNP)
                      <Select
                        data-testid="campaign-connectivity-partner-select"
                        disabled
                        id="campaign_connectivity_partner"
                        {...input}
                        options={campaignConnectivityPartnerOptions}
                        placeholder="Select a connectivity partner"
                        style={{ display: 'block', width: '100%' }}
                      />
                    </label>
                  )}
                </Field>
              </form>
            </Modal>
          )}
        />
      )}
    </>
  )
}
